/** Suppliers and customers.
 *
 * TWO masters, not one party master with roles — FKMT's choice on 29 August
 * 2026, made knowing that the same organisation is sometimes both. The
 * recovered data bears it out: GOLDHOFER AKTIENGESELLSCHAFT is supplier 1 and
 * customer 4 in GHERP, and FKMT is content with the duplication.
 *
 * The code is generated and hidden. In GHERP `SuppCode` held the same text as
 * `SuppName` — "GOLDHOFER AKTIENGESELLSCHAFT" in both — so it carried no
 * information at all. The name is what people use.
 *
 * Nine dead columns from GHERP are not carried forward: TINNO, CSTNO, ECCNO,
 * SERTAXINV, SERTAX, EXDUTY, VAT, SALETAXNO and a second four-line freight
 * address. All pre-GST, all empty since July 2017. Ledger D21.
 */

import type { MasterSpec } from '@/types'

export const SUPPLIERS: MasterSpec = {
  module: 'masters', object: 'supplier', title: 'Suppliers',
  singular: 'Supplier',
  path: 'masters/suppliers', idKey: 'supplier_code',
  idGenerated: true,
  idGeneratedHelp: 'Issued by the system — suppliers are known by name',
  searchPlaceholder: 'Search by name, city, GSTIN or contact…',
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'city', label: 'City', width: '140px' },
    { key: 'gstin', label: 'GSTIN', width: '160px' },
    { key: 'contact_person', label: 'Contact', width: '160px' },
    { key: 'is_subcontractor', label: 'Job Work', width: '100px' },
  ],
  fields: [
    { key: 'name', label: 'Name', required: true },
    { key: 'legal_name', label: 'Legal Name',
      help: 'If it differs from the trading name' },
    // A job work order goes to a subcontractor, and the system should not
    // have to guess which suppliers those are.
    { key: 'is_subcontractor', label: 'Does Job Work', type: 'select',
      options: [
        { value: 'false', label: 'No' },
        { value: 'true', label: 'Yes — operations are sent to them' },
      ],
      help: 'Only these appear when raising a job work order' },
    { key: 'supplier_type', label: 'Type',
      help: 'Manufacturer, trader, service — your own wording' },

    { key: 'contact_person', label: 'Contact Person' },
    { key: 'phone', label: 'Phone' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'email', label: 'E-mail', type: 'email' },
    { key: 'website', label: 'Website' },

    { key: 'address', label: 'Address', type: 'textarea' },
    { key: 'city', label: 'City' },
    { key: 'state_code', label: 'GST State Code',
      help: 'Two digits — 29 for Karnataka' },
    { key: 'pincode', label: 'PIN Code' },
    { key: 'country', label: 'Country' },

    { key: 'gstin', label: 'GSTIN', help: '15 characters' },
    { key: 'pan', label: 'PAN' },
    { key: 'currency_code', label: 'Currency', help: 'INR unless they invoice otherwise' },

    { key: 'payment_terms', label: 'Payment Terms', value: true,
      help: 'Default for their purchase orders — overridable on each one' },
    { key: 'delivery_terms', label: 'Delivery Terms', value: true },
    { key: 'note', label: 'Note', type: 'textarea' },
  ],
}

export const CUSTOMERS: MasterSpec = {
  module: 'masters', object: 'customer', title: 'Customers',
  singular: 'Customer',
  path: 'masters/customers', idKey: 'customer_code',
  idGenerated: true,
  idGeneratedHelp: 'Issued by the system — customers are known by name',
  searchPlaceholder: 'Search by name, city, GSTIN or contact…',
  columns: [
    { key: 'name', label: 'Name' },
    { key: 'category', label: 'Vertical', width: '130px' },
    { key: 'city', label: 'City', width: '140px' },
    { key: 'gstin', label: 'GSTIN', width: '160px' },
    { key: 'contact_person', label: 'Contact', width: '160px' },
  ],
  fields: [
    { key: 'name', label: 'Name', required: true },
    { key: 'legal_name', label: 'Legal Name' },
    // Carried from the project management app, where the boundary is already
    // enforced: a Fabrication BOM may only be raised against a Fabrication
    // customer, because Trailer customers are transporters.
    { key: 'category', label: 'Vertical', type: 'select', required: true,
      options: [
        { value: 'Fabrication', label: 'Fabrication' },
        { value: 'Trailer', label: 'Trailer — transporters' },
      ],
      help: 'Decides which work can be raised against them' },

    { key: 'contact_person', label: 'Contact Person' },
    { key: 'phone', label: 'Phone' },
    { key: 'mobile', label: 'Mobile' },
    { key: 'email', label: 'E-mail', type: 'email' },

    { key: 'address', label: 'Address', type: 'textarea' },
    { key: 'city', label: 'City' },
    { key: 'state_code', label: 'GST State Code',
      help: 'Two digits — 29 for Karnataka' },
    { key: 'pincode', label: 'PIN Code' },
    { key: 'country', label: 'Country' },

    { key: 'gstin', label: 'GSTIN', help: '15 characters' },
    { key: 'pan', label: 'PAN' },
    { key: 'currency_code', label: 'Currency' },

    { key: 'payment_terms', label: 'Payment Terms', value: true },
    { key: 'note', label: 'Note', type: 'textarea' },
  ],
}
